import { useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Papa from "papaparse";
import { Button } from "@direct-campaign/ui";
import {
  REQUIRED_VOTER_FIELDS,
  suggestMapping,
  validateMapping,
  VOTER_FIELDS,
} from "@direct-campaign/utils";
import type {
  ColumnMapping,
  VoterFieldKey,
  VoterImportSummary,
} from "@direct-campaign/types";
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  FileSpreadsheet,
  Loader2,
  Upload,
} from "lucide-react";
import { apiFetch } from "../../lib/api";

type Step = "upload" | "map" | "importing" | "done";

const SAMPLE_ROW_COUNT = 3;

export function VoterImportPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>("upload");
  const [file, setFile] = useState<File | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [sampleRows, setSampleRows] = useState<Record<string, string>[]>([]);
  const [mapping, setMapping] = useState<ColumnMapping>({});
  const [parseError, setParseError] = useState("");
  const [importError, setImportError] = useState("");
  const [summary, setSummary] = useState<VoterImportSummary | null>(null);
  const [dragActive, setDragActive] = useState(false);

  const mappingProblems = useMemo(() => validateMapping(mapping), [mapping]);
  const mappedFields = useMemo(
    () => new Set(Object.values(mapping).filter((v): v is VoterFieldKey => v !== null)),
    [mapping],
  );

  function handleFile(selected: File) {
    setParseError("");
    if (!/\.(csv|txt)$/i.test(selected.name)) {
      setParseError("Please choose a .csv file.");
      return;
    }

    Papa.parse<Record<string, string>>(selected, {
      header: true,
      skipEmptyLines: true,
      preview: 25,
      complete: (results) => {
        const fields = (results.meta.fields ?? []).filter((f) => f.trim() !== "");
        if (fields.length === 0) {
          setParseError("Couldn't find a header row in that file.");
          return;
        }
        setFile(selected);
        setHeaders(fields);
        setSampleRows(results.data);
        setMapping(suggestMapping(fields));
        setStep("map");
      },
      error: () => setParseError("Couldn't read that file. Is it a valid CSV?"),
    });
  }

  async function handleImport() {
    if (!file || mappingProblems.length > 0) return;
    setStep("importing");
    setImportError("");

    // Order matters: the API streams the file, so the mapping must arrive first.
    const form = new FormData();
    form.append("mapping", JSON.stringify(mapping));
    form.append("file", file);

    try {
      const res = await apiFetch("/api/voters/import", { method: "POST", body: form });
      if (res.ok) {
        setSummary((await res.json()) as VoterImportSummary);
        setStep("done");
      } else {
        const data = (await res.json().catch(() => null)) as
          | { message?: string; error?: string }
          | null;
        setImportError(data?.message ?? data?.error ?? "Import failed. Please try again.");
        setStep("map");
      }
    } catch {
      setImportError("Couldn't reach the server. Check your connection and try again.");
      setStep("map");
    }
  }

  function reset() {
    setStep("upload");
    setFile(null);
    setHeaders([]);
    setSampleRows([]);
    setMapping({});
    setSummary(null);
    setParseError("");
    setImportError("");
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center gap-3">
        <Link
          to="/dashboard/voters"
          className="text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Import voters</h2>
          <p className="text-muted-foreground mt-1">
            Upload a voter file CSV and map its columns to voter fields.
          </p>
        </div>
      </div>

      {step === "upload" && (
        <div className="space-y-4">
          {parseError && (
            <div className="rounded-md bg-destructive/10 border border-destructive/20 px-4 py-3 text-sm text-destructive">
              {parseError}
            </div>
          )}

          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={() => setDragActive(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragActive(false);
              const dropped = e.dataTransfer.files[0];
              if (dropped) handleFile(dropped);
            }}
            onClick={() => fileInputRef.current?.click()}
            className={[
              "rounded-lg border-2 border-dashed p-12 text-center cursor-pointer transition-colors",
              dragActive ? "border-primary bg-primary/5" : "border-input hover:border-primary/50",
            ].join(" ")}
          >
            <Upload className="w-8 h-8 mx-auto text-muted-foreground" />
            <p className="mt-3 font-medium">Drop your CSV here, or click to browse</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Any voter file format works — you&apos;ll map the columns in the next step.
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.txt,text/csv"
              className="hidden"
              onChange={(e) => {
                const selected = e.target.files?.[0];
                if (selected) handleFile(selected);
                e.target.value = "";
              }}
            />
          </div>

          <p className="text-xs text-muted-foreground">
            Files are deduplicated automatically: rows with a Direct Campaign voter ID update the
            matching voter; rows without one are matched by name, address, and date of birth.
            New voters are assigned a voter ID (DC-XXXXXXXX) on import.
          </p>
        </div>
      )}

      {step === "map" && file && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <FileSpreadsheet className="w-4 h-4" />
            <span className="font-medium text-foreground">{file.name}</span>
            <span>· {headers.length} columns</span>
            <button onClick={reset} className="ml-2 text-primary hover:underline">
              Choose a different file
            </button>
          </div>

          {importError && (
            <div className="rounded-md bg-destructive/10 border border-destructive/20 px-4 py-3 text-sm text-destructive">
              {importError}
            </div>
          )}

          {/* Required-field checklist */}
          <div className="flex flex-wrap gap-2">
            {REQUIRED_VOTER_FIELDS.map((key) => {
              const def = VOTER_FIELDS.find((f) => f.key === key);
              const done = mappedFields.has(key);
              return (
                <span
                  key={key}
                  className={[
                    "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium",
                    done
                      ? "border-green-200 bg-green-50 text-green-800"
                      : "border-amber-200 bg-amber-50 text-amber-800",
                  ].join(" ")}
                >
                  {done ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                  {def?.label ?? key}
                </span>
              );
            })}
          </div>

          <div className="rounded-lg border overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/40">
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground w-1/4">
                    CSV column
                  </th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                    Sample values
                  </th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground w-56">
                    Imports as
                  </th>
                </tr>
              </thead>
              <tbody>
                {headers.map((header) => {
                  const samples = sampleRows
                    .map((row) => row[header])
                    .filter((v): v is string => Boolean(v && v.trim()))
                    .slice(0, SAMPLE_ROW_COUNT);
                  const value = mapping[header] ?? "";
                  return (
                    <tr key={header} className="border-b last:border-0">
                      <td className="px-4 py-3 font-medium align-top">{header}</td>
                      <td className="px-4 py-3 text-muted-foreground align-top">
                        <span className="line-clamp-2">
                          {samples.length > 0 ? samples.join(" · ") : <em>empty</em>}
                        </span>
                      </td>
                      <td className="px-4 py-2 align-top">
                        <select
                          value={value}
                          onChange={(e) =>
                            setMapping((m) => ({
                              ...m,
                              [header]: (e.target.value || null) as VoterFieldKey | null,
                            }))
                          }
                          className={[
                            "flex h-9 w-full rounded-md border bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
                            value ? "border-input" : "border-input text-muted-foreground",
                          ].join(" ")}
                        >
                          <option value="">Don&apos;t import</option>
                          {VOTER_FIELDS.map((f) => (
                            <option key={f.key} value={f.key}>
                              {f.label}
                              {f.required ? " *" : ""}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {mappingProblems.length > 0 && (
            <div className="rounded-md bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800 space-y-1">
              {mappingProblems.map((p) => (
                <p key={p}>{p}</p>
              ))}
            </div>
          )}

          <div className="flex items-center gap-2">
            <Button onClick={() => void handleImport()} disabled={mappingProblems.length > 0}>
              Import voters
            </Button>
            <Button variant="ghost" onClick={reset}>
              Cancel
            </Button>
            <p className="text-xs text-muted-foreground ml-2">
              Every original column is preserved with each voter, even unmapped ones.
            </p>
          </div>
        </div>
      )}

      {step === "importing" && (
        <div className="rounded-lg border bg-card p-12 text-center space-y-3">
          <Loader2 className="w-8 h-8 mx-auto animate-spin text-primary" />
          <p className="font-medium">Importing {file?.name}…</p>
          <p className="text-sm text-muted-foreground">
            Large files can take a minute. Keep this page open.
          </p>
        </div>
      )}

      {step === "done" && summary && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <SummaryCard label="Rows in file" value={summary.totalRows} />
            <SummaryCard label="New voters" value={summary.imported} tone="positive" />
            <SummaryCard label="Updated" value={summary.updated} />
            <SummaryCard
              label="Skipped"
              value={summary.skipped}
              tone={summary.skipped > 0 ? "warning" : undefined}
            />
          </div>

          {(summary.errors.length > 0 || summary.warnings.length > 0) && (
            <div className="rounded-lg border overflow-hidden">
              <div className="px-4 py-3 border-b bg-muted/40 text-sm font-medium">
                Issues{summary.issuesTruncated ? " (first 100 shown)" : ""}
              </div>
              <table className="w-full text-sm">
                <tbody>
                  {summary.errors.map((issue) => (
                    <tr key={`e-${issue.row}-${issue.message}`} className="border-b last:border-0">
                      <td className="px-4 py-2 w-24 text-muted-foreground">Row {issue.row}</td>
                      <td className="px-4 py-2 w-28">
                        <span className="text-xs font-medium text-destructive">Skipped</span>
                      </td>
                      <td className="px-4 py-2">{issue.message}</td>
                    </tr>
                  ))}
                  {summary.warnings.map((issue) => (
                    <tr key={`w-${issue.row}-${issue.message}`} className="border-b last:border-0">
                      <td className="px-4 py-2 w-24 text-muted-foreground">Row {issue.row}</td>
                      <td className="px-4 py-2 w-28">
                        <span className="text-xs font-medium text-amber-700">Warning</span>
                      </td>
                      <td className="px-4 py-2">{issue.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex gap-2">
            <Button onClick={() => void navigate("/dashboard/voters")}>View voters</Button>
            <Button variant="outline" onClick={reset}>
              Import another file
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: "positive" | "warning" | undefined;
}) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <p
        className={[
          "text-2xl font-bold tracking-tight",
          tone === "positive" ? "text-green-700" : tone === "warning" ? "text-amber-700" : "",
        ].join(" ")}
      >
        {value.toLocaleString()}
      </p>
      <p className="text-xs text-muted-foreground mt-1">{label}</p>
    </div>
  );
}
